/**
 * What the Admin RENDERS, decided in one table (admin spec 4.2, 4.3, 7).
 *
 * THIS IS NOT AUTHORIZATION. requireRole and the feature checks on the API
 * decide what is ALLOWED (api/src/authRoutes.ts, api/src/adminRoutes.ts); the
 * merged /admin-api/me handler says so itself: "the SPA hides what the role
 * cannot do based on this. Authorization itself is always server-side -- this
 * is a rendering hint, never a permission." Every control this module hides
 * must ALSO be a 403 or a 503 if it is called anyway, and Task 14's checklist
 * proves it for the owner-only surface.
 *
 * ORDER OF REASONS IS LOAD-BEARING: role, then feature, then billing. A
 * manager on a past_due tenant must be told "keine Berechtigung" for the
 * owner-only tab, not "Zahlung überfällig" -- they cannot see the invoice and
 * paying it would not open the tab.
 *
 * ┌─ RECORDED DISCREPANCY (phase-4 final review §8, M-1; plan 3c amendment E3)
 * │ The BILLING gate below is CLIENT-SIDE ONLY, on this surface as on the
 * │ scanner. POST /admin-api/actions/ebay-sync checks features.ebay and never
 * │ tenant.status (api/src/adminRoutes.ts:1598-1600), so a past_due tenant who
 * │ reaches the endpoint another way still runs the job. That is a phase-6/7
 * │ billing item and is deliberately NOT fixed here -- a server-side status
 * │ gate is a change to the app repo with its own blast radius (the cron path,
 * │ the queue consumer and the scanner all hit the same job bodies).
 * │ What this package does instead: surface the billing state prominently
 * │ (Task 8's banner) so the state is never a surprise.
 * └────────────────────────────────────────────────────────────────────────────
 */
import type { AdminRole, Features, TenantStatus, TenantSummary } from './types';

export const SURFACES = [
  'staff',          // Mitarbeiter, Geräte
  'adminUsers',     // the owner-only admin-user tab (admin spec D7)
  'sheet',          // Tabelle
  'masterData',     // Stammdaten
  'ebayConnect',    // connect / disconnect / status
  'ebayActions',    // sync, listings, review refresh, publish, SKU sync
  'priceScanAction',
  /**
   * The DATEV Bankenimport download (Task 13 review M4).
   *
   * ITS OWN SURFACE, and the reason is the whole point: it is feature-gated on
   * `ebay` exactly as `ebayActions` is, and it is NOT paused by billing --
   * because the server does not pause it either. `runDatevPreview`
   * (api/src/adminRoutes.ts:1513-1516) checks `featuresOf(ctx.tenant).ebay` and
   * reads no `tenant.status` at all, so hiding the control under `past_due`
   * would invent a refusal the platform does not make and would break this
   * module's own contract that every control it hides is ALSO a 403/503.
   *
   * The product reading agrees with the mechanical one: pausing a SYNC is
   * pausing a write that spends platform quota on a tenant who is not paying.
   * A DATEV export is a READ of payout data the tenant has already earned, it
   * costs one bounded pass, and it is needed most acutely by the business whose
   * books are in trouble. Withholding an accounting export over an unpaid
   * invoice is leverage, not fairness.
   */
  'datev',
  'jobs',
  'metrics',
] as const;
export type Surface = (typeof SURFACES)[number];

export type GateReason = 'ok' | 'role' | 'feature' | 'billing';

/** Spec 7: "eBay sync pauses"; locked/canceled are strictly worse than past_due. */
export const EBAY_PAUSED_STATUSES: readonly TenantStatus[] = ['past_due', 'locked', 'canceled'];

/**
 * The gate below checks membership in THIS allowlist, not the paused
 * blocklist above -- fail-closed (review I-1). `TenantStatus` is a 5-literal
 * union, but nothing at the API boundary enforces that at runtime: api.ts's
 * `adminFetch` does an unchecked `body as T` cast with no schema validation,
 * so `tenant.status` is not actually guaranteed to be one of the five
 * literals when it reaches here. An unrecognized string or `undefined` must
 * PAUSE eBay, not silently render it as available -- so the condition has to
 * be "status is not known-healthy", never "status is known-paused".
 */
const EBAY_HEALTHY_STATUSES = new Set<TenantStatus>(['trial', 'active']);

const OWNER_ONLY = new Set<Surface>(['adminUsers']);
const NEEDS_FEATURE: Partial<Record<Surface, keyof Features>> = {
  ebayActions: 'ebay',
  priceScanAction: 'priceScan',
  // Same flag as `ebayActions`, and deliberately absent from PAUSED_BY_BILLING
  // below -- see the `datev` entry in SURFACES for why the two differ.
  datev: 'ebay',
};
/**
 * `ebayActions` ONLY. Spec 7 says "eBay sync pauses"; a sync is a write that
 * spends platform quota. Nothing else in SURFACES is a write of that kind, and
 * `datev` is explicitly a read the server never gates on status.
 */
const PAUSED_BY_BILLING = new Set<Surface>(['ebayActions']);

export interface CapabilityCtx {
  role: AdminRole;
  status: TenantStatus;
  features: Features;
}

export function gateReason(surface: Surface, ctx: CapabilityCtx): GateReason {
  if (OWNER_ONLY.has(surface) && ctx.role !== 'owner') return 'role';
  const flag = NEEDS_FEATURE[surface];
  if (flag && !ctx.features[flag]) return 'feature';
  // ebayConnect is deliberately absent from both maps above: gating the
  // connect on the flag the connect exists to enable would make eBay
  // unreachable for every new tenant.
  //
  // Deliberately NOT `EBAY_PAUSED_STATUSES.includes(ctx.status)`: that would
  // fail OPEN for any status outside the closed union (review I-1). Checking
  // the healthy allowlist's *absence* fails CLOSED instead -- an unknown or
  // missing status pauses eBay rather than rendering it as available.
  if (PAUSED_BY_BILLING.has(surface) && !EBAY_HEALTHY_STATUSES.has(ctx.status)) return 'billing';
  return 'ok';
}

export function isAllowed(surface: Surface, ctx: CapabilityCtx): boolean {
  return gateReason(surface, ctx) === 'ok';
}

const DAY_MS = 86_400_000;

export type BannerKind = 'none' | 'trial' | 'past_due' | 'locked' | 'canceled' | 'unknown';

/**
 * SILENCE IS RESERVED FOR `active`, for the same fail-closed reason as the
 * gate above (review I-1, then m-1). The gate pauses eBay for any status
 * outside {trial, active}, including an unrecognised string and `undefined` --
 * both reachable, because api.ts casts the response body with no runtime
 * validation. If the banner treated those as healthy, the owner would see the
 * eBay actions dead with nothing on screen explaining why, which is the worst
 * of both readings. `unknown` says the honest thing instead.
 */
export function billingBanner(
  tenant: TenantSummary,
  nowMs: number,
): { kind: BannerKind; daysLeft: number | null } {
  if (tenant.status === 'active') return { kind: 'none', daysLeft: null };
  if (tenant.status === 'past_due') return { kind: 'past_due', daysLeft: null };
  if (tenant.status === 'locked') return { kind: 'locked', daysLeft: null };
  if (tenant.status === 'canceled') return { kind: 'canceled', daysLeft: null };
  if (tenant.status === 'trial') {
    if (!tenant.trialEndsAt) return { kind: 'trial', daysLeft: null };
    const ms = Date.parse(tenant.trialEndsAt) - nowMs;
    // Rounded UP, floored at 0: "noch 1 Tag" for anything still running today,
    // and never a negative count for a trial that already lapsed.
    return { kind: 'trial', daysLeft: Math.max(0, Math.ceil(ms / DAY_MS)) };
  }
  return { kind: 'unknown', daysLeft: null };
}
